import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import FormatResourceStatus from "go-app/components/FormatResourceStatus/FormatResourceStatus";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { mapToBase } from "go-core/api/searchSelectService";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { FormatAddressString } from "go-core/components/Formatters/FormatAddress";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import Tag from "../../../../../../../../components/Common/Tag/Tag";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { PartnerApi } from "../../../../../../../../services/Api/Partner/partnerTypes";
import { api } from "../../../../../../../../services/Security/api";
import { selectOrganization } from "../../../../../../../../services/organizations/selectors";
import { mapResponseToResponseDataWithId } from "../../../../services/mapResponseToResponseDataWithId";

interface ListState {
	resource?: Record<string, any>;
	mobileActions: MobileActionProps[];
}

const listName = "ADMIN_PARTNER";
const resourceType = "ADMIN_PARTNER";

const List = ({ resource, mobileActions }: ListState) => {
	const { t } = useTranslation("admin");
	const confirmation = useConfirmation();
	const { addSuccessFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const segmentContextValue = React.useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();
	const onRemovePartner = async (item: PartnerApi) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.remove", { ns: "lib" }),
		});
		await adminApi.removeAdminPartner(item.id);
		addSuccessFlash(t("lib:common.flash.removed"));
	};
	const onRestorePartner = async (item: PartnerApi) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.activate", { ns: "lib" }),
		});
		await adminApi.restoreAdminPartner(item.id);
		addSuccessFlash(t("lib:common.flash.restored"));
	};
	const onEnablePartner = async (item: PartnerApi) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.activate", { ns: "lib" }),
		});
		await adminApi.enableAdminPartner(item.id);
		addSuccessFlash(t("lib:common.flash.completed"));
	};
	const onDisablePartner = async (item: PartnerApi) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.disable", { ns: "lib" }),
		});
		await adminApi.disableAdminPartner(item.id);
		addSuccessFlash(t("lib:common.flash.completed"));
	};
	let config = {
		fields: [
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "text",
				render: (item: PartnerApi) => {
					return (
						<div className={"d-flex flex-column"}>
							<RenderLimitedText minWidth={300}>
								<Link to={`/partnership/${item.id}`}>{item.name}</Link>
							</RenderLimitedText>
							{item.slug && <small>{item.slug}</small>}
							{item.tags.length > 0 && (
								<div className="d-flex flex-wrap">
									{item.tags.map((tag, index) => (
										<Tag key={index}>{tag}</Tag>
									))}
								</div>
							)}
						</div>
					);
				},
				renderExport: (item: PartnerApi) => `${item.name} ${item.slug ? `(${item.slug})` : ""}`,
			},
			{
				id: "type",
				name: t("lib:common.word.type"),
				type: "list",
				options: {
					RESELLER: "Reseller",
					KAM: "KAM",
					TOP: "TOP",
					SALE: "Sale",
					CS: "CS",
					CSV: "CSv",
					SALE_PREMIUM: "Sale premium",
					AFFILIATE: "AFFILIATE",
				},
			},
			{
				id: "company_details",
				name: t("common.word.company_info"),
				disableSorting: true,
				render: (item: PartnerApi) => {
					return (
						<RenderLimitedText minWidth={250}>
							<div className={"d-flex flex-column"}>
								<span>{item.company_name}</span>
								<span className={"text-detailed"}>
									{FormatAddressString({
										street: item.company_street,
										build_nr: item.company_build_nr,
										flat_nr: item.company_flat_nr,
										zip_code: item.company_zip_code,
										city: item.company_city,
									})}
								</span>
								{item.company_tax_id_no && (
									<small className={"text-detailed"}>NIP: {item.company_tax_id_no}</small>
								)}
							</div>
						</RenderLimitedText>
					);
				},
				renderExport: (item: PartnerApi) => {
					return `${item.company_name ? item.company_name : ""} ${
						item.company_tax_id_no ? ` ${item.company_tax_id_no}` : ""
					}${item.company_tax_id_no || item.company_name ? ", " : ""} ${FormatAddressString({
						street: item.company_street,
						build_nr: item.company_build_nr,
						flat_nr: item.company_flat_nr,
						zip_code: item.company_zip_code,
						city: item.company_city,
					})}`;
				},
			},
			{
				id: "company_contact",
				name: t("common.word.contact_info"),
				disableSorting: true,
				render: (item: PartnerApi) => {
					return (
						<RenderLimitedText minWidth={250}>
							<div className={"d-flex flex-column"}>
								<span>{item.company_name}</span>
								{item.company_phone_number && (
									<span className={"text-detailed"}>{item.company_phone_number}</span>
								)}
								{item.company_email && <span className={"text-detailed"}>{item.company_email}</span>}
							</div>
						</RenderLimitedText>
					);
				},
				renderExport: (item: PartnerApi) =>
					`${item.contact_person ?? ""} ${item.company_phone_number ?? ""} ${item.company_email ?? ""}`,
			},
			{
				id: "keeper",
				name: t("modules.partner.field.keeper.title"),
				type: "text",
			},
			{
				id: "parent",
				name: t("modules.partner.field.payoff_partner.title"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getListAdminPartnersSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
				render: (item: PartnerApi) => {
					if (!item.parent) return null;
					return (
						<RenderLimitedText minWidth={200}>
							<Link to={`/partnership/${item.parent.id}`}>{item.parent.name}</Link>
						</RenderLimitedText>
					);
				},
				renderExport: (item: PartnerApi) => item?.parent?.name,
			},
			{
				id: "provision",
				name: t("common.word.provision"),
				type: "number",
				render: (partner: PartnerApi) => {
					const provisions = partner.provisions?.length ? partner.provisions : partner.default_provisions;
					if (provisions) {
						return (
							<div className={"d-flex flex-column"}>
								{provisions.map((prov) => {
									return (
										<>
											<span>{prov.reference_id}</span>
											<small className="text-muted font-weight-bold text-nowrap">
												{t("common.word.billed_period")} {prov.billing_period}
											</small>
											<small className="text-muted font-weight-bold text-nowrap">
												{t("common.word.provision_value")}&nbsp;
												{prov.type === "PERCENT"
													? `${prov.provision}%`
													: FormatMoney({
															amount: prov.provision,
															currency: "PLN",
													  })}
											</small>
										</>
									);
								})}
							</div>
						);
					}
					return null;
				},
				renderExport: (item: PartnerApi) => {
					const provisions = item.provisions ? item.provisions : item.default_provisions;
					return provisions.map((p) => p.reference_id);
				},
			},
			{
				id: "balance",
				name: t("modules.partner.field.balance.title"),
				type: "number",
				render: (item: PartnerApi) => `${FormatMoney(item.balance)}`,
			},
			{
				id: "code",
				name: t("modules.partner.field.code.title"),
				type: "text",
				render: (item: PartnerApi) => {
					return (
						item.codes.length > 0 && (
							<div className="d-flex flex-wrap">
								{item.codes.map((tag, index) => (
									<Tag key={index}>{tag}</Tag>
								))}
							</div>
						)
					);
				},
				renderExport: (item: PartnerApi) => item.codes.join(", "),
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
				render: (item: PartnerApi) => FormatDate(item.created_at),
			},
			{
				id: "status",
				name: t("lib:common.word.status"),
				type: "list",
				options: {
					ENABLED: t("enums.common.status.ENABLED", { ns: "lib" }),
					DISABLED: t("enums.common.status.DISABLED", { ns: "lib" }),
					DELETED: t("enums.common.status.DELETED", { ns: "lib" }),
				},
				render: (item: PartnerApi) => <FormatResourceStatus status={item.status} />,
				renderExport: (item: PartnerApi) => t(`lib:enums.common.status.${item.status}`),
			},
			{
				id: "user",
				name: t("lib:common.word.users"),
				type: "search_select",
				render: (item: PartnerApi) => {
					return (
						<RenderLimitedText minWidth={300}>
							{item?.users?.map((user) => user.user_email).join(", ")}
						</RenderLimitedText>
					);
				},
				renderExport: (item: PartnerApi) => item.users?.map((user) => user.user_email).join(", "),
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.user().getUsersSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
		],
		filters: [
			{
				id: "organization",
				name: t("lib:common.word.locations"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.user().getOrganizationsSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "company_name",
				name: t("common.word.company_name"),
				type: "text",
			},
			{
				id: "company_tax_id_no",
				name: t("lib:common.word.tax_id_no.title"),
				type: "text",
			},
			{
				id: "company_city",
				name: t("modules.partner.field.company_city.title"),
				type: "text",
			},
			{
				id: "tag",
				name: t("common.word.tags"),
				type: "search_select",
				source: {
					request: async (search: string) => {
						const f = mapToBase(search, { resource_type: "PARTNER" });
						return mapResponseToResponseDataWithId(await adminApi.getTagsListSearchSelect(f));
					},
				},
			},
			{
				id: "provision_type",
				name: t("modules.partner.field.provision_type.title"),
				type: "list",
				options: {
					AMOUNT: t("enums.provisions.type.AMOUNT"),
					PERCENT: t("enums.provisions.type.PERCENT"),
				},
			},
		],
		actions: [
			{
				name: t("common.action.preview", { ns: "lib" }),
				link: (item: PartnerApi) => `/partnership/${item.id}`,
			},
			{
				name: t("common.action.edit", { ns: "lib" }),
				link: (item: PartnerApi) => `/admin/partners/${item.id}`,
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: PartnerApi) => onRemovePartner(item),
				visible: (item: PartnerApi) => item.status !== "DELETED",
				refreshListAfter: true,
			},
			{
				name: t("lib:common.action.activate"),
				click: (item: PartnerApi) => onRestorePartner(item),
				visible: (item: PartnerApi) => item.status === "DELETED",
				refreshListAfter: true,
			},
			{
				name: t("common.action.deactivate"),
				click: (item: PartnerApi) => onDisablePartner(item),
				visible: (item: PartnerApi) => item.status === "ENABLED",
				refreshListAfter: true,
			},
			{
				name: t("lib:common.action.activate"),
				click: (item: PartnerApi) => onEnablePartner(item),
				visible: (item: PartnerApi) => item.status === "DISABLED",
				refreshListAfter: true,
			},
		],
		selectedColumns: [
			"name",
			"type",
			"company_details",
			"keeper",
			"parent",
			"provision",
			"company_contact",
			"balance",
			"code",
			"created_at",
			"user",
		],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all", { ns: "lib" }),
			},
			{
				id: "disabled",
				name: t("modules.partner.field.disabled.title"),
				slug: "disabled",
				filters: [
					{
						filterId: "status",
						value: "DISABLED",
						condition: "e",
					},
				],
			},
			{
				id: "deleted",
				name: "Usunięci",
				slug: "deleted",
				filters: [
					{
						filterId: "status",
						value: "DELETED",
						condition: "e",
					},
				],
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include =
				"users,organizations,tags,parent,provisions,codes,default_provisions,custom_fields,balance";
			return adminApi.getAdminPartners(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		exportConfig: {
			title: t("modules.partner.header.title"),
			filename: t("modules.partner.field.export_config.filename"),
			organization: `${organization.name}`,
		},
		numberOfStickyColumnsAtTheStart: 1,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
		filterValues: data.filter_values,
	};

	return <ListData config={config} mobileActions={mobileActions} />;
};

const AdminPartnersList = () => {
	const { t } = useTranslation("admin");
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const customFieldResourceTypes = [
		{
			type: "PARTNER",
			name: t(`enums.custom_fields.resources.PARTNER`),
		},
	];
	const isMobile = useWindowSize().isMobile;
	const buttons: ButtonProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: "/admin/partners/new",
			variant: "primary",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: "/admin/partners/new",
		},
	];
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(`${t("modules.partner.header.title")} | ${t("lib:go_component.navigation.admin")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	return (
		<div className="content">
			{!isMobile && <Header title={t("modules.partner.header.title")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} mobileActions={mobileActions} />
			</Suspense>
		</div>
	);
};

export default AdminPartnersList;
